import {Box, Button, ExpandableSection, FormField, Grid, Input, Tabs, TabsProps} from '@amzn/awsui-components-react'
import React, {useContext, useEffect, useState} from 'react'
import {isValidNumericId} from '../../validation/InputValidator'
import {useHistory} from 'react-router-dom'
import * as queryString from 'query-string'
import {MetadataKey, MetadataQuery} from '../../backend/model'
import {PropertyFilterQuery} from '@amzn/awsui-collection-hooks'
import GlobalDataContext from '../../context/GlobalDataProvider'
import {
    convertToQuery,
    convertToPropertyFilterQuery,
    getPropertyFiltersFromMetadataKeys
} from '../search/SearchUtils'
import {ADVANCED_SEARCH_TAB_ID, SIMPLE_SEARCH_TAB_ID} from '../metadata/Constants'
import {fetchIogMetadataKeys} from "../../backend/backend"
import AdvancedSearch from '../search/AdvancedSearch'
import {METADATA_I18N_STRINGS} from '../metadata/MetadataSearchProperty'

interface IogSearchPanelProps {
    iogId?: string
    fcId?: string
    initialTab?: string
    advancedSearchQuery?: MetadataQuery
    simpleSearchHandler: (iogId?: string, fcId?: string) => void
    advancedSearchHandler: (query: MetadataQuery) => void
}

const IogSearchPanel: React.FC<IogSearchPanelProps> = (props) => {
    const [iogId, setIogId] = useState(props.iogId)
    const [fcId, setFcId] = useState(props.fcId)
    const {iogMetadataKeys, setIogMetadataKeys} = useContext(GlobalDataContext)
    const history = useHistory()
    const displayedQuery = convertToPropertyFilterQuery(props.advancedSearchQuery!)

    useEffect(() => {
        if (iogMetadataKeys.length === 0) {
            fetchMetadataKeys().then(metadataKeysResponse => {
                setIogMetadataKeys(metadataKeysResponse)
            })
        }
    }, [iogMetadataKeys])

    useEffect(() => {
        setIogId(props.iogId)
        setFcId(props.fcId)
    }, [props])

    function executeSimpleSearchOnEnter(key: string): void {
        if (key === 'Enter') {
            props.simpleSearchHandler(iogId, fcId)
        }
    }

    async function fetchMetadataKeys(): Promise<MetadataKey[]> {
        return (await fetchIogMetadataKeys()).data.map((metadataKey) => metadataKey.iogMetadataKeyObject)
    }

    const metadataSearchHandler = (query: PropertyFilterQuery) => {
        let iogQuery = convertToQuery(query)
        props.advancedSearchHandler(iogQuery)
    }

    const simpleSearch = (
        <Grid gridDefinition={[{colspan: 12}, {colspan: 12}, {colspan: 12}]}>
            <Grid gridDefinition={[{colspan: 4}, {colspan: 4}, {colspan: 4}]}>
                <FormField label="IOG ID" stretch>
                    <Input
                        id="iogSearchPanel-iogId"
                        invalid={!!iogId && !isValidNumericId(iogId)}
                        value={iogId || ''}
                        onChange={({detail}) => setIogId(detail.value.trim())}
                        onKeyDown={({detail}) => executeSimpleSearchOnEnter(detail.key)}
                    />
                </FormField>
                <FormField label="FC ID (optional)" stretch>
                    <Input
                        id="iogSearchPanel-fcId"
                        value={fcId || ''}
                        onChange={({detail}) => setFcId(detail.value.trim())}
                        onKeyDown={({detail}) => executeSimpleSearchOnEnter(detail.key)}
                    />
                </FormField>
                <Box margin={{top: 'xl'}}>
                    <Button variant="primary" onClick={() => props.simpleSearchHandler(iogId, fcId)}>Search</Button>
                </Box>
            </Grid>
        </Grid>
    )

    const [filteringProperties, filteringOptions] = getPropertyFiltersFromMetadataKeys(iogMetadataKeys)

    const advancedSearch = (
        <AdvancedSearch label={"Metadata Keys"}
            searchHandler={metadataSearchHandler}
            displayedQuery={displayedQuery}
            filteringProperties={filteringProperties}
            filteringOptions={filteringOptions}
            i18nStrings={METADATA_I18N_STRINGS}
        />
    )

    const handleTabSwitch = (detail: TabsProps.ChangeDetail) => {
        const basePath = '/iogs'

        const newUrl = queryString.stringifyUrl({
            url: basePath,
            query: {
                view: detail.activeTabId,
            },
        })
        history.push(newUrl)
    }

    const searchTab = (
        <Tabs
            onChange={({detail}) => handleTabSwitch(detail)}
            activeTabId={props.initialTab}
            tabs={[
                {
                    label: 'Simple Search',
                    id: SIMPLE_SEARCH_TAB_ID,
                    content: simpleSearch,
                },
                {
                    label: 'Advanced Search',
                    id: ADVANCED_SEARCH_TAB_ID,
                    content: advancedSearch,
                },
            ]}
        />
    )

    return (
        <ExpandableSection
            id="iogSearchPanel"
            headerText="Search"
            variant="container"
            defaultExpanded>
            {searchTab}
        </ExpandableSection>
    )
}

export default IogSearchPanel
